import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Search, Filter, Edit, Trash2, Star } from 'lucide-react';
import Layout from '../components/Layout';
import RateForm from '../components/RateForm';
import { rateService } from '../services/rateService';
import { useAuth } from '../context/AuthContext';

const Pricing = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rateToDelete, setRateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const categories = [
    { value: 'all', label: 'Todas las tarifas' },
    { value: 'Mensual', label: 'Mensuales' },
    { value: 'Anual', label: 'Anuales' },
    { value: 'Eventos', label: 'Eventos' }
  ];

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      setLoading(true);
      const response = await rateService.getRates();
      // Si viene paginado, extraer data
      const rates = response.data || response;
      setPricingPlans(Array.isArray(rates) ? rates : []);
    } catch (error) {
      console.error('Error al cargar tarifas:', error);
      setPricingPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = pricingPlans.filter(plan => {
    const matchesSearch = plan.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plan.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || plan.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCreatePlan = () => {
    setEditingRate(null);
    setShowForm(true);
  };

  const handleEditPlan = (id) => {
    const rate = pricingPlans.find(p => p.id === id);
    setEditingRate(rate);
    setShowForm(true);
  };

  const handleDeletePlan = (id) => {
    const rate = pricingPlans.find(p => p.id === id);
    setRateToDelete(rate);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!rateToDelete) return;

    try {
      setDeleting(true);
      await rateService.deleteRate(rateToDelete.id);
      await loadRates();
      setShowDeleteModal(false);
      setRateToDelete(null);
    } catch (error) {
      console.error('Error al eliminar tarifa:', error);
      alert('Error al eliminar la tarifa. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setRateToDelete(null);
  };

  const handleToggleActive = async (id) => {
    try {
      const rate = pricingPlans.find(p => p.id === id);
      if (!rate) return;

      const updatedRate = await rateService.updateRate(id, {
        ...rate,
        active: !rate.active
      });

      setPricingPlans(prevPlans =>
        prevPlans.map(plan => plan.id === id ? updatedRate : plan)
      );
    } catch (error) {
      console.error('Error al actualizar estado de tarifa:', error);
      alert('Error al actualizar el estado. Inténtalo de nuevo.');
    }
  };

  const handleFormSuccess = () => {
    loadRates();
    setShowForm(false);
    setEditingRate(null);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRate(null);
  };

  return (
    <Layout title="Tarifas" description="Gestiona las tarifas y planes de tu club">
      {/* Header con botón de crear */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Planes de Tarifas</h2>
          <p className="text-gray-600">Gestiona los diferentes planes y precios de tu club</p>
        </div>
        <button
          onClick={handleCreatePlan}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus size={20} className="mr-2" />
          Nueva Tarifa
        </button>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar tarifas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando tarifas...</p>
        </div>
      )}

      {/* Listado de tarifas */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md ${
                plan.is_popular ? 'border-blue-500' : 'border-gray-200'
              } ${!plan.active ? 'opacity-60' : ''}`}
            >
              {/* Header de la tarifa */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full mt-1">
                      {plan.category}
                    </span>
                    {plan.club && (
                      <span className="inline-block bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full mt-1 ml-2">
                        {plan.club.name}
                      </span>
                    )}
                  </div>
                  {plan.is_popular && (
                    <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                      <Star size={12} className="mr-1" />
                      Popular
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-lg text-gray-600 ml-1">{plan.currency || '€'}</span>
                    <span className="text-gray-500 ml-1">/{plan.period}</span>
                  </div>
                  {plan.description && (
                    <p className="text-gray-600 text-sm mt-1">{plan.description}</p>
                  )}
                </div>
              </div>

              {/* Características */}
              <div className="p-6">
                {plan.features && plan.features.length > 0 ? (
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-700">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 mb-6">Sin características definidas</p>
                )}

                {/* Acciones */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditPlan(plan.id)}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center text-sm"
                  >
                    <Edit size={16} className="mr-1" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleToggleActive(plan.id)}
                    className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                      plan.active
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                    }`}
                  >
                    {plan.active ? 'Activa' : 'Inactiva'}
                  </button>
                  <button
                    onClick={() => handleDeletePlan(plan.id)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mensaje si no hay tarifas */}
      {!loading && filteredPlans.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <CreditCard size={64} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron tarifas</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedCategory !== 'all'
              ? 'No hay tarifas que coincidan con los filtros aplicados.'
              : 'Aún no has creado ninguna tarifa para tu club.'
            }
          </p>
          <button
            onClick={handleCreatePlan}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Crear Primera Tarifa
          </button>
        </div>
      )}

      {/* Modal de formulario */}
      <RateForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        rateData={editingRate}
        isEditing={!!editingRate}
      />

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && rateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar eliminación</h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar la tarifa <strong>{rateToDelete.name}</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Eliminando...
                  </>
                ) : (
                  'Eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Pricing;
